import { VersionRange } from '../../models/core/version-range.js';
import { DataModelBuilder, SerializationContext, SerializedData, isSameData } from '../../models/dto/dto.js';
import { Address } from '../../models/serializable/address.js';
import { PhoneNumber } from '../../models/serializable/phone-number.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Option } from '../../utility/option.js';
import { Component } from '../core/component.js';

export class LegacyBillingDetailsComponent extends Component {
  static readonly TYPE_ID: string = 'a503e34f5ece4f47bad09ec69ba020ee';
  static readonly SCHEMA_VERSION: number = 5;

  private constructor(
    public readonly name: string | null,
    public readonly email: string | null,
    public readonly phoneNumber: PhoneNumber | null,
    public readonly address: Address | null,
  ) {
    super();
  }

  get $removable(): boolean {
    return false;
  }

  get $typeId(): string {
    return LegacyBillingDetailsComponent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(LegacyBillingDetailsComponent.SCHEMA_VERSION);
  }

  static create(options: {
    name?: string | null,
    email?: string | null,
    phoneNumber?: PhoneNumber | null,
    address?: Address | null,
  }): LegacyBillingDetailsComponent {
    return new LegacyBillingDetailsComponent(
        options.name ?? null,
        options.email ?? null,
        options.phoneNumber ?? null,
        options.address ?? null,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): LegacyBillingDetailsComponent {
    return new LegacyBillingDetailsComponent(
        MapUtils.getOrNull<string>(map, 'name'),
        MapUtils.getOrNull<string>(map, 'email'),
        PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber')),
        context.fromDataOrNull<Address>(MapUtils.getOrNull<SerializedData>(map, 'address')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'name': this.name,
      'email': this.email,
      'phoneNumber': this.phoneNumber?.encode() ?? null,
      'address': context.toDataOrNull(this.address),
    };
  }

  clone(): this {
    return new LegacyBillingDetailsComponent(
        this.name,
        this.email,
        this.phoneNumber?.clone() ?? null,
        this.address?.clone() ?? null,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.name === this.name &&
        other.email === this.email &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        isSameData(other.address, this.address);
  }

  copyWith(fields: {
    name?: Option<string | null>,
    email?: Option<string | null>,
    phoneNumber?: Option<PhoneNumber | null>,
    address?: Option<Address | null>,
  }): LegacyBillingDetailsComponent {
    const name = fields.name ?? Option.none();
    const email = fields.email ?? Option.none();
    const phoneNumber = fields.phoneNumber ?? Option.none();
    const address = fields.address ?? Option.none();

    return new LegacyBillingDetailsComponent(
        name.isSome ? name.value : this.name,
        email.isSome ? email.value : this.email,
        phoneNumber.isSome ? phoneNumber.value : this.phoneNumber,
        address.isSome ? address.value : this.address,
    );
  }
}

export class LegacyBillingDetailsComponentBuilder extends DataModelBuilder<LegacyBillingDetailsComponent> {
  build(context: SerializationContext, map: SerializedData): LegacyBillingDetailsComponent {
    return LegacyBillingDetailsComponent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(LegacyBillingDetailsComponent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(LegacyBillingDetailsComponent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2, we added the [stripeCustomerId] field.
    if(version < 2) version = 2;

    // In version 3, we added the [billingoPartnerId] field.
    if(version < 3) version = 3;

    // In version 4, we added the [address] field.
    if(version < 4) version = 4;

    // In version 5, we removed the [stripeCustomerId] and [billingoPartnerId] fields.
    if(version < 5) version = 5;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
